import { SearchOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Select, Table, Tooltip } from "antd";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { createUseStyles } from "react-jss";
import useSystemNoticeApi, { SYSTEM_NOTICE_QUERIES } from "../../apiHooks/useSystemNoticeApi.js";
import { systemNoticeColumns } from "../config/systemNotice-columns";

const PRIORITY_OPTIONS = [
  { label: "All Priorities", value: null },
  { label: "General", value: "General" },
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];

const HEADER_OPTIONS = [
  { label: "All Headers", value: null },
  { label: "Import", value: "Import" },
  { label: "Export", value: "Export" },
  { label: "Other", value: "Other" },
];

const useStyles = createUseStyles({
  wrapper: { display: "flex", flexDirection: "column", gap: 16 },

  filterCard: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e4edf6",
    boxShadow: "0 2px 12px rgba(27,42,107,0.06)",
    padding: "12px 16px",
  },
  filterRow: {
    display: "flex",
    flexWrap: "nowrap",
    gap: 10,
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    "&": {
      borderRadius: "10px !important",
      height: "36px !important",
      fontSize: "0.875rem !important",
      background: "#f0f6fb !important",
      border: "1px solid #dde8f4 !important",
      boxShadow: "none !important",
    },
    "&:focus-within, &.ant-input-affix-wrapper-focused": {
      borderColor: "#00A9E0 !important",
      boxShadow: "0 0 0 2px rgba(0,169,224,0.12) !important",
    },
    "& .ant-input": { background: "transparent !important", fontSize: "0.875rem !important" },
  },
  filterSelect: {
    height: "36px !important",
    "&.ant-select-single, &.ant-select": { height: "36px !important" },
    "& .ant-select-selector": {
      borderRadius: "10px !important",
      fontSize: "0.875rem !important",
      height: "36px !important",
      alignItems: "center !important",
      paddingInline: "14px !important",
      background: "#f0f6fb !important",
      border: "1px solid #dde8f4 !important",
    },
    "& .ant-select-selection-item": { lineHeight: "34px !important" },
    "&.ant-select-focused .ant-select-selector": {
      borderColor: "#00A9E0 !important",
      boxShadow: "0 0 0 2px rgba(0,169,224,0.12) !important",
    },
  },
  refreshBtn: {
    borderRadius: "10px !important",
    border: "1.5px solid #93c5fd !important",
    color: "#3b82f6 !important",
    background: "#ffffff !important",
    height: "36px !important",
    width: "44px !important",
    padding: "0 !important",
    flexShrink: "0 !important",
    "&:hover": { background: "#3b82f6 !important", borderColor: "#3b82f6 !important", color: "#ffffff !important" },
  },

  tableCard: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e4edf6",
    boxShadow: "0 2px 12px rgba(27,42,107,0.06)",
    overflow: "hidden",
  },
  tableWrap: {
    "& .ant-table-thead > tr > th": {
      background: "#f5f9fd !important",
      color: "#1B2A6B !important",
      fontWeight: "700 !important",
      fontSize: "0.78rem !important",
      letterSpacing: "0.04em !important",
      textTransform: "uppercase !important",
      borderTop: "1px solid #e4edf6 !important",
      borderBottom: "1px solid #e4edf6 !important",
      borderRadius: "0 !important",
      padding: "12px 16px !important",
    },
    "& .ant-table-thead > tr > th:first-child, & .ant-table-thead > tr > th:last-child": {
      borderRadius: "0 !important",
    },
    "& .ant-table-tbody > tr > td": {
      borderBottom: "1px solid #f0f5fb !important",
      padding: "10px 16px !important",
      fontSize: "0.875rem !important",
      color: "#1a1d2e !important",
    },
    "& .ant-table-tbody > tr:hover > td": { background: "#f7fbff !important" },
    "& .ant-table-tbody > tr:nth-child(even) > td": { background: "#fafcff" },
    "& .ant-table-tbody > tr:nth-child(even):hover > td": { background: "#f7fbff !important" },
    "& .ant-table": { border: "none !important" },
    "& .ant-table-container": { border: "none !important" },
    "& .ant-pagination": {
      padding: "10px 20px !important",
      margin: "0 !important",
      borderTop: "1px solid #edf3fa !important",
    },
  },
});

const EMPTY = { search: "", priority: null, postingHeader: null };

const SystemNoticeTable = forwardRef(({ actionCellRenderer }, ref) => {
  const classes = useStyles();
  const [filters, setFilters] = useState(EMPTY);

  const {
    listSystemNoticesApi: { data = [], execute, isLoading },
  } = useSystemNoticeApi([SYSTEM_NOTICE_QUERIES.listSystemNotices]);

  useImperativeHandle(ref, () => ({ refresh() { execute(); } }));

  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    return data.filter((x) => {
      const matchSearch = s ? (x.subject ?? "").toLowerCase().includes(s) : true;
      const matchPriority = filters.priority ? x.priority === filters.priority : true;
      const matchHeader = filters.postingHeader ? x.postingHeader === filters.postingHeader : true;
      return matchSearch && matchPriority && matchHeader;
    });
  }, [data, filters]);

  const set = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  return (
    <div className={classes.wrapper}>
      <div className={classes.filterCard}>
        <div className={classes.filterRow}>
          <Input
            className={classes.searchInput}
            prefix={<SearchOutlined style={{ color: "#9ab8cf" }} />}
            placeholder="Search by subject..."
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            allowClear
          />
          <Select
            className={classes.filterSelect}
            style={{ width: 170, flexShrink: 0 }}
            options={PRIORITY_OPTIONS}
            value={filters.priority}
            onChange={(v) => set("priority", v)}
          />
          <Select
            className={classes.filterSelect}
            style={{ width: 180, flexShrink: 0 }}
            options={HEADER_OPTIONS}
            value={filters.postingHeader}
            onChange={(v) => set("postingHeader", v)}
          />
          <Tooltip placement="top" title="Refresh">
            <Button
              className={classes.refreshBtn}
              loading={isLoading}
              icon={<UndoOutlined />}
              onClick={() => { execute(); setFilters(EMPTY); }}
              style={{ width: 36, height: 36 }}
            />
          </Tooltip>
        </div>
      </div>

      <div className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <Table
            size="small"
            columns={systemNoticeColumns(actionCellRenderer)}
            dataSource={filtered}
            loading={isLoading}
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 10,
              pageSizeOptions: [5, 10, 15, 20, 25],
              showSizeChanger: true,
              position: ["bottomRight"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default SystemNoticeTable;
